import { useLoanApplication } from '@axo/shared/data-access/hooks';
import { AceCardSection } from '../../../AceCard';
import { useId } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { currencyFormatter } from '@axo/shared/util/formatters';
import styles from './DebtCategory.module.css';
import { useApplicationDataStore } from '../../../../store/applicationDataStore';
import { Button } from '@blueprintjs/core';
import { DebtType } from '@axo/shared/data-access/types';
import { ApplicationDataFormValues } from '../../ApplicationDataForm/ApplicationDataFormSchema';
import { ApplicationDataCurrencyField } from '../../Fields/ApplicationDataCurrencyField';
import { ApplicationDataTextField } from '../../Fields/ApplicationDataTextField';
import { ApplicationDataNumberField } from '../../Fields/ApplicationDataNumberField';
import { MarketCountryCode } from '@axo/shared/types';
import { useApplicationConditionalContext } from '../../../ConditionalWrappers/useApplicationConditionalContext';
import { ConditionalWrapper } from '../../../ConditionalWrappers/ConditionalWrapper';

interface DebtCategoryProps {
  applicationID: string;
  category: DebtType;
  coApplicantCustomerID: string | undefined;
  coApplicantPersonID: string | undefined;
}

export const DebtCategory: React.FC<DebtCategoryProps> = ({
  applicationID,
  category,
  coApplicantCustomerID,
  coApplicantPersonID,
}) => {
  const isCoApplicant = coApplicantPersonID || coApplicantCustomerID;
  const fieldArrayName: 'CoDebts' | 'Debts' = isCoApplicant
    ? 'CoDebts'
    : 'Debts';

  const applicationConditionalContext = useApplicationConditionalContext();

  const id = useId();
  const { data: applicationData } = useLoanApplication(applicationID);
  const { isEditing } = useApplicationDataStore(applicationID);
  const { watch } = useFormContext<ApplicationDataFormValues>();
  const [debts] = watch([fieldArrayName]);
  const { remove } = useFieldArray<ApplicationDataFormValues>({
    name: fieldArrayName,
  });

  const totalRefinanceAmount = debts
    .filter(({ Type }) => Type === category)
    .reduce((sum, { RefinanceAmount }) => sum + (RefinanceAmount ?? 0), 0);

  const totalOutstandingAmount = debts
    .filter(({ Type }) => Type === category)
    .reduce((sum, { OutstandingAmount }) => sum + (OutstandingAmount ?? 0), 0);

  if (!applicationData) return null;

  const currency = applicationData.Currency ?? undefined;

  return (
    <AceCardSection
      id={id}
      title={category}
      isExpandedByDefault={false}
      tag={[
        currencyFormatter(totalRefinanceAmount, currency, 0),
        currencyFormatter(totalOutstandingAmount, currency, 0),
      ].join(' / ')}
    >
      <div className={styles.mainContainer}>
        {debts.map((item, index) => {
          if (item.Type !== category) return null;

          const ctx = { ...applicationConditionalContext, itemType: item.Type };

          return (
            <div key={item.ID || index} className={styles.itemContainer}>
              <ApplicationDataCurrencyField
                applicationID={applicationID}
                name={`${fieldArrayName}.${index}.OutstandingAmount`}
                label="Outstanding amount"
              />
              <ApplicationDataCurrencyField
                applicationID={applicationID}
                name={`${fieldArrayName}.${index}.MonthlyDebtPayment`}
                label="Monthly pay"
              />
              <ConditionalWrapper
                context={ctx}
                blacklist={[
                  {
                    marketCountry: [MarketCountryCode.Denmark],
                    itemType: [
                      DebtType.Car,
                      DebtType.Collection,
                      DebtType.Housing,
                      DebtType.Student,
                    ],
                  },
                ]}
              >
                <ApplicationDataCurrencyField
                  applicationID={applicationID}
                  name={`${fieldArrayName}.${index}.RefinanceAmount`}
                  label="Refinance amount"
                />
              </ConditionalWrapper>
              <ConditionalWrapper
                context={ctx}
                whitelist={[
                  {
                    marketCountry: [MarketCountryCode.Denmark],
                  },
                  {
                    itemType: [DebtType.Housing],
                    marketCountry: [MarketCountryCode.Finland],
                  },
                  {
                    itemType: [DebtType.Personal],
                    marketCountry: [MarketCountryCode.Norway],
                  },
                ]}
              >
                <ApplicationDataTextField
                  name={`${fieldArrayName}.${index}.Creditor`}
                  applicationID={applicationID}
                  label="Creditor"
                />
              </ConditionalWrapper>
              <ConditionalWrapper
                context={ctx}
                whitelist={[
                  {
                    itemType: [DebtType.Personal],
                    marketCountry: [MarketCountryCode.Norway],
                  },
                ]}
              >
                <ApplicationDataNumberField
                  name={`${fieldArrayName}.${index}.NominalInterestRate`}
                  applicationID={applicationID}
                  label="Nominal interest rate"
                  isEditable={false}
                  formatter={(value: unknown) => {
                    const n = Number(value);

                    if (isNaN(n)) return '-';

                    return `${n / 100}%`;
                  }}
                />
              </ConditionalWrapper>
              {isEditing && (
                <Button icon="trash" onClick={() => remove(index)}>
                  Remove debt item
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </AceCardSection>
  );
};
